import { computed, type MaybeRefOrGetter, toValue } from 'vue';
import { storeToRefs } from 'pinia';
import { matchesAnyBaseSku } from '@/composables/productHelper';
import { useProductCompleteStore } from '@/features/Product/stores/productComplete';
import { useCartStore } from '@/features/Cart/stores/cart';
import { codeProductData } from '@/features/Product/stores/rawData/codeProductData';
import { CartItemQuantity, CartRule } from '@/features/Cart/types/models';
import { ShopifyCartItem } from '@/features/Cart/types/servicesShopifyCart';

export type VariantParentMaxQuantityFamily = {
  parentSku: string;
  parentTitle: string;
  maxQuantity: number;
  memberSkus: string[];
};

export type FamilyCountLine = Pick<ShopifyCartItem, 'sku' | 'quantity' | 'properties'>;

type BundleSkuQuantity = {
  sku: string;
  quantity: number;
};

const getBundleSkuQuantities = (item: FamilyCountLine): BundleSkuQuantity[] => {
  const customBundleDetails = item.properties?._custom_bundle_details;
  if (!customBundleDetails) return [];
  try {
    const bundleItems: { sku: string; quantity?: number }[] = JSON.parse(customBundleDetails);
    return bundleItems.map((bundleItem) => ({
      sku: bundleItem.sku,
      quantity: bundleItem.quantity ?? 1,
    }));
  } catch {
    return [];
  }
};

export const findVariantParentMaxQuantityFamilies = (): VariantParentMaxQuantityFamily[] => {
  const productCompleteStore = useProductCompleteStore();

  return productCompleteStore.cachedDisplayProducts
    .filter((displayProduct) => (displayProduct.attributes.variantDetails?.ids?.length ?? 0) > 0)
    .map((displayProduct): VariantParentMaxQuantityFamily | null => {
      const parent = productCompleteStore.getCompleteProduct(
        'attributes.sku',
        displayProduct.attributes.sku,
      );
      if (!parent) return null;
      const maxQuantity = parent.attributes.maxCartQuantity;
      if (!Number.isFinite(maxQuantity) || maxQuantity === 5) {
        return null;
      }
      const familyTag: `variant-family-${string}` = `variant-family-${parent.attributes.handle}`;
      const taggedMembers = [...codeProductData.values()]
        .filter((candidate) => candidate.attributes.productTags?.includes(familyTag))
        .map((candidate) => candidate.attributes.sku);

      const memberSkus = [
        parent.attributes.sku,
        ...(parent.variantProducts ?? []).map((variantProduct) => variantProduct.attributes.sku),
        ...taggedMembers,
      ];

      return {
        parentSku: parent.attributes.sku,
        parentTitle: parent.content.title,
        maxQuantity,
        memberSkus,
      };
    })
    .filter((family): family is VariantParentMaxQuantityFamily => family !== null);
};

const getFamilyUnitsPerLine = (
  item: FamilyCountLine,
  family: VariantParentMaxQuantityFamily,
): number => {
  if (matchesAnyBaseSku(item.sku, family.memberSkus)) {
    return 1;
  }
  return getBundleSkuQuantities(item)
    .filter(({ sku }) => matchesAnyBaseSku(sku, family.memberSkus))
    .reduce((total, { quantity }) => total + quantity, 0);
};

const findMatchingLines = <T extends FamilyCountLine>(
  items: T[],
  family: VariantParentMaxQuantityFamily,
) =>
  items
    .map((item) => ({
      item,
      unitsPerLine: getFamilyUnitsPerLine(item, family),
    }))
    .filter(({ unitsPerLine }) => unitsPerLine > 0);

export const getFamilyTotalQuantity = (
  items: FamilyCountLine[],
  family: VariantParentMaxQuantityFamily,
): number =>
  findMatchingLines(items, family).reduce(
    (total, { item, unitsPerLine }) => total + item.quantity * unitsPerLine,
    0,
  );

export const cartRuleVariantParentMaxQuantity: CartRule = (cart) => {
  const families = findVariantParentMaxQuantityFamilies();
  if (!families.length) return null;

  const update: CartItemQuantity[] = [];
  const remove: CartItemQuantity[] = [];

  families.forEach((family) => {
    const matchingLines = findMatchingLines(cart.items, family);
    const totalQuantity = getFamilyTotalQuantity(cart.items, family);
    if (totalQuantity <= family.maxQuantity) return;
    let remaining = family.maxQuantity;
    matchingLines.forEach(({ item, unitsPerLine }) => {
      if (remaining <= 0) {
        remove.push({
          key: item.key,
          quantity: 0,
        });
        return;
      }

      const keepableLineQuantity = Math.floor(remaining / unitsPerLine);
      if (keepableLineQuantity <= 0) {
        remove.push({
          key: item.key,
          quantity: 0,
        });
        return;
      }
      if (keepableLineQuantity < item.quantity) {
        update.push({
          key: item.key,
          quantity: keepableLineQuantity,
        });

        remaining -= keepableLineQuantity * unitsPerLine;
        return;
      }
      remaining -= item.quantity * unitsPerLine;
    });
  });

  return update.length || remove.length
    ? {
        add: [],
        update,
        remove,
      }
    : null;
};

export const useMaxCartQuantityMessages = () => {
  const { cartData } = storeToRefs(useCartStore());
  const messages = computed(() => {
    return findVariantParentMaxQuantityFamilies()
      .map((family) => {
        const totalQuantity = getFamilyTotalQuantity(cartData.value.items, family);
        return {
          family,
          totalQuantity,
        };
      })
      .filter(({ family, totalQuantity }) => totalQuantity >= family.maxQuantity)
      .map(({ family }) => ({
        parentSku: family.parentSku,
        message: `Max quantity of ${family.maxQuantity}x ${family.parentTitle} per order.`,
      }));
  });

  return { messages };
};

export const useCartItemVariantParentMaxQuantity = (
  cartItem: MaybeRefOrGetter<ShopifyCartItem | undefined | null>,
) => {
  const { cartData } = storeToRefs(useCartStore());
  const remainingQuantity = computed<number | undefined>(() => {
    const item = toValue(cartItem);
    if (!item) return undefined;

    const family = findVariantParentMaxQuantityFamilies().find(
      (candidateFamily) => getFamilyUnitsPerLine(item, candidateFamily) > 0,
    );
    if (!family) return undefined;

    const unitsPerLine = getFamilyUnitsPerLine(item, family);
    const otherUnits = findMatchingLines(cartData.value.items, family)
      .filter(({ item: cartLine }) => cartLine.key !== item.key)
      .reduce((total, { item: cartLine, unitsPerLine: cartLineUnits }) => {
        return total + cartLine.quantity * cartLineUnits;
      }, 0);

    const availableUnits = family.maxQuantity - otherUnits;
    const maxLineQuantity = Math.floor(availableUnits / unitsPerLine);
    return Math.max(item.quantity, maxLineQuantity);
  });

  return { remainingQuantity };
};
